/**
 * Cadastro interno de influencer — cartão com arte alternativa (ver
 * `passbook.hasInfluencerCard` / `user.isInfluencer` na legacy-api).
 *
 * Listar usa o mesmo endpoint que `store-users` (`POST /users`, `User.list_by_type`),
 * com o filtro novo `isInfluencer` (só retorna quem tem a flag). Cadastrar usa o
 * mesmo endpoint autenticado que o app usa pra cadastro rápido por telefone/CPF
 * (`POST /auth/new-member`), com `defaultBalance`/`isInfluencer` no corpo — a
 * legacy-api só honra esses dois campos quando quem chama é Admin ou está
 * mirando um passbook com `hasInfluencerCard: true` (ver
 * `AuthenticationController.new_register`), então a checagem de acesso real é
 * lá, não aqui: o guard de tela (`canAccessInfluencerRegistration`) é só UX.
 */
import { legacy } from '@/shared/api/client';
import { onlyDigits } from '@/shared/lib/format';

/** Documento de usuário como a legacy-api devolve em `POST /users`. */
export interface InfluencerUser {
  _id: string;
  email?: string;
  phone?: string;
  profile?: {
    name?: string;
    uniqueId?: string;
  };
}

function passbookHeaders(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

export async function fetchInfluencers(idPassbook: string): Promise<InfluencerUser[]> {
  const data = await legacy.post<InfluencerUser[]>(
    '/users',
    { id_passbook: idPassbook, role: ['Member'], isInfluencer: true },
    passbookHeaders(idPassbook),
  );
  return Array.isArray(data) ? data : [];
}

export interface InfluencerRegistrationPayload {
  idPassbook: string;
  name: string;
  email?: string;
  phone: string;
  cpf: string;
  /** DD/MM/AAAA — igual ao cadastro público. */
  birthday?: string;
  /** 'M' | 'F' | 'O' | 'P' — igual ao cadastro público. */
  gender?: string;
  /** Saldo inicial de selos. 0/ausente = sem crédito na criação. */
  defaultBalance?: number;
}

interface NewMemberResponse {
  user?: { _id?: string };
}

export async function registerInfluencer(payload: InfluencerRegistrationPayload): Promise<void> {
  await legacy.post<NewMemberResponse>(
    '/auth/new-member',
    {
      action: 'insert',
      id_passbook: payload.idPassbook,
      name: payload.name.trim(),
      email: payload.email?.trim() || undefined,
      phone: payload.phone,
      uniqueId: onlyDigits(payload.cpf),
      birthday: payload.birthday || undefined,
      gender: payload.gender || undefined,
      defaultBalance: payload.defaultBalance && payload.defaultBalance > 0 ? payload.defaultBalance : undefined,
      isInfluencer: true,
    },
    passbookHeaders(payload.idPassbook),
  );
}
